import {SphereItem} from './sphere_item';
import {Planogram} from './planogram';
import {CLUSTER_NAME_REGEX} from './shared/constants';
import {isContentOverlayAction, SphereItemType} from 'shared/interfaces/planogram';
import {ImageMetaData, ClusterMetaData, MediaMetaData, ProductMetaData} from './interfaces/planogram.interface';
import {AppUtils} from './utils/app_utils';
import loadingProgress, {LOADING_STAGES} from './api/services/loading_progress.service';
import {Group} from 'three';
import LodProvider from 'shared/lod/LodProvider';
import VideoItem from './video_item';

function fetchVideoAspectRatio(url: string): Promise<number> {
  const videoElm = document.createElement('video');
  videoElm.preload = 'metadata';
  videoElm.src = url;
  videoElm.load();
  return new Promise(resolve => {
    videoElm.onloadedmetadata = () => {
      videoElm.remove();
      resolve(videoElm.videoWidth / videoElm.videoHeight);
    };
  });
}

export class SphereItems extends Group {
  readonly items: Array<SphereItem> = [];

  constructor(private planogram: Planogram, lodProvider: LodProvider) {
    super();
    this.layers.enable(2);

    this.items = this.planogram.items.map(item => {
      if (item.type === SphereItemType.Video) return new VideoItem(item, this.planogram, lodProvider);
      else return new SphereItem(item, this.planogram, lodProvider);
    });
    this.items.forEach(it => this.add(it.object3D));

    loadingProgress.progressStage(LOADING_STAGES.ITEM_MESHES, 1);
  }

  dispose() {
    this.items.forEach(it => it.dispose());
  }

  findClusterByClusterName(clusterName: string): SphereItem | undefined {
    // handle case with raw value as cluster name
    if (clusterName.match(CLUSTER_NAME_REGEX)) {
      clusterName = AppUtils.extractClusterName(clusterName);
    }
    const foundClusters = this.items.filter(item => {
      return (item.data as ClusterMetaData).clusterLink === clusterName && item.type === SphereItemType.Cluster;
    });
    if (foundClusters.length) {
      if (foundClusters.length > 1) {
        console.error('There are more than one cluster with the same name');
      }
      return foundClusters[0];
    }
  }

  findSphereItemByIdentifier(identifier: string): SphereItem | undefined {
    return this.items.find(item => {
      return item.identifier === identifier || item.id?.toString() === identifier;
    });
  }

  findSphereItemByActionIdentifier(identifier: string) {
    return this.items.find(item => {
      return item.identifier === identifier || item.id?.toString() === identifier;
    });
  }

  findSphereItemByImageId(imageId: string) {
    return this.items.find(
      item =>
        (item.data as ProductMetaData).picture?.id?.toString() === imageId ||
        (item.data as ImageMetaData).id?.toString() === imageId ||
        item.id?.toString() === imageId
    );
  }

  findSphereItemByContentLink(link: string) {
    return this.items.find(
      it => it.action !== undefined && isContentOverlayAction(it.action) && it.action.data.iframeLink === link
    );
  }
}
